import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/errors';
import type { FormDocument } from '@/api/generated/client';
import type { FieldPlacement } from '@/types/template-fields';

/**
 * Form Document with joined template name.
 * Re-exports the generated FormDocument type which already includes
 * the optional `formTemplateName` field from the OpenAPI schema.
 */
export type FormDocumentWithTemplateName = FormDocument;

/**
 * Data path mapping for form fields to claim data
 * Maps field keys to their corresponding paths in the claim data object
 */
const FIELD_DATA_PATHS: Record<string, string> = {
  // Insured / Homeowner
  insured_name: 'insuredName',
  insured_address: 'propertyAddress',
  mailing_address: 'mailingAddress',
  insured_phone: 'insuredPhone',
  insured_phone2: 'insuredPhone2',
  insured_email: 'insuredEmail',
  insured_email2: 'insuredEmail2',
  // Claim
  claim_number: 'claimNumber',
  policy_number: 'policyNumber',
  insurance_carrier: 'insuranceCarrier',
  type_of_loss: 'lossType',
  date_of_loss: 'dateOfLoss',
  contractor: 'contractor',
  // System
  current_date: '_system.current_date',
};

/**
 * Utility to get nested value from an object using dot notation path
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (current && typeof current === 'object' && key in (current as Record<string, unknown>)) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Hook to manage form documents for a claim
 * Provides CRUD operations and template management
 */

export function useFormDocuments(claimId: number) {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    // Fetch form documents for the claim
    // The generated client properly deserializes formTemplateName and Date fields
    const { data: response, isLoading } = useQuery({
        queryKey: ['form-documents', claimId],
        queryFn: async () => {
            return await apiClient.formTemplates.getFormDocumentsByClaim({ claimId });
        },
        enabled: !!claimId,
    });

    // Fetch active templates for dropdown (user's own company templates)
    const { data: templatesResponse, isLoading: isLoadingTemplates } = useQuery({
        queryKey: ['form-templates-active'],
        queryFn: async () => {
            return await apiClient.formTemplates.listFormTemplates({ includeInactive: false });
        },
    });

    // Send form to homeowner
    const sendFormMutation = useMutation({
        mutationFn: async ({ 
            formTemplateId, 
            recipientName, 
            recipientEmail 
        }: { 
            formTemplateId: number; 
            recipientName: string; 
            recipientEmail: string;
        }) => {
            return await apiClient.formTemplates.sendFormToHomeowner({
                claimId,
                sendFormDocumentRequest: {
                    formTemplateId,
                    recipientName,
                    recipientEmail,
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['form-documents', claimId] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            toast({
                title: 'Form sent',
                description: 'The form has been sent to the homeowner.',
            });
        },
        onError: async (error: unknown) => {
            toast({
                title: 'Failed to send form',
                description: await getApiErrorMessage(error),
                variant: 'destructive',
            });
        },
    });

    // Cancel form document
    const cancelFormMutation = useMutation({
        mutationFn: async (formDocId: number) => {
            return await apiClient.formTemplates.cancelFormDocument({
                claimId,
                formDocId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['form-documents', claimId] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            toast({
                title: 'Form cancelled',
                description: 'The form has been cancelled.',
            });
        },
        onError: async (error: unknown) => {
            toast({
                title: 'Failed to cancel form',
                description: await getApiErrorMessage(error),
                variant: 'destructive',
            });
        },
    });

    // Delete a form document (completed or cancelled forms only)
    const deleteFormMutation = useMutation({
        mutationFn: async (formDocId: number) => {
            await apiClient.formTemplates.deleteFormDocument({
                claimId,
                formDocId,
            });
            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['form-documents', claimId] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            toast({
                title: 'Form deleted',
                description: 'The form has been deleted.',
            });
        },
        onError: async (error: unknown) => {
            toast({
                title: 'Failed to delete form',
                description: await getApiErrorMessage(error),
                variant: 'destructive',
            });
        },
    });

    const formDocuments = response?.documents || [];
    const templates = templatesResponse?.templates || [];

    return {
        formDocuments,
        isLoading,
        templates,
        isLoadingTemplates,
        sendForm: sendFormMutation.mutate,
        sendFormAsync: sendFormMutation.mutateAsync,
        isSending: sendFormMutation.isPending,
        cancelForm: cancelFormMutation.mutate,
        isCancelling: cancelFormMutation.isPending,
        deleteForm: deleteFormMutation.mutate,
        deleteFormAsync: deleteFormMutation.mutateAsync,
        isDeleting: deleteFormMutation.isPending,
    };
}

/**
 * Hook to resolve field values from claim data
 * Maps form field keys to actual claim data values
 */
export function useFormFieldResolver(claimData: Record<string, unknown>) {
  /**
   * Resolve a single field value based on its configuration
   * Handles data fields, system fields, and user-fillable fields
   */
  const resolveFieldValue = useCallback((field: FieldPlacement): string => {
    // User-fillable field (text_input or signature) - no default value
    if (field.type === 'text_input' || field.type === 'signature') {
      return '';
    }

    // System field for current date
    // Field palette uses 'current_date'; accept '_system.current_date' for backward compatibility
    if (field.fieldKey === 'current_date' || field.fieldKey === '_system.current_date') {
      return new Date().toLocaleDateString();
    }

    // Data field - look up value from claim data
    const dataPath = FIELD_DATA_PATHS[field.fieldKey];
    if (!dataPath) {
      console.warn(`No data path mapping for field key: ${field.fieldKey}`);
      return '';
    }

    const value = getNestedValue(claimData, dataPath);

    // Handle date formatting
    // Append T00:00:00 so the string is parsed as local time, not UTC.
    // ISO date-only strings (YYYY-MM-DD) are parsed as UTC by spec, which causes
    // off-by-one-day errors for all US timezones. Adding a local time component fixes this.
    if (value !== null && value !== undefined && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        return new Date(`${value}T00:00:00`).toLocaleDateString();
      } catch {
        return String(value);
      }
    }

    // Return stringified value, treating only null/undefined as missing
    return value !== null && value !== undefined ? String(value) : '';
  }, [claimData]);

  /**
   * Resolve all field values in a list
   * Returns a map of field IDs to their resolved values
   */
  const resolveAllFields = useCallback((fields: FieldPlacement[]): Record<string, string> => {
    const resolved: Record<string, string> = {};
    for (const field of fields) {
      resolved[field.id] = resolveFieldValue(field);
    }
    return resolved;
  }, [resolveFieldValue]);

  return {
    resolveFieldValue,
    resolveAllFields,
  };
}
